/**
 * Metric 19: Notes to Management
 * 
 * A persistent free-text field for executive notes.
 * Features:
 * - Rich text editor with basic formatting (Bold, Bullet points)
 * - Auto-saves on blur
 * - Admins can edit, other roles read-only
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, Spin, Alert, message } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  RichTextEditorComponent,
  Toolbar,
  HtmlEditor,
  Link,
  QuickToolbar,
  Inject,
} from '@syncfusion/ej2-react-richtexteditor';

import type { RootState } from '@/store';
import {
  getDashboardMetric19,
  updateDashboardMetric19,
} from '@/services/dashboardService';

// ==================== STYLED COMPONENTS ====================

const RichTextEditorWrapper = styled.div`
  .e-richtexteditor {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
  }

  .e-richtexteditor .e-rte-content {
    min-height: 200px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1f2937;
  }

  .e-richtexteditor .e-rte-content .e-content {
    padding: 16px;
  }

  .e-richtexteditor .e-toolbar {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  .e-richtexteditor .e-toolbar .e-toolbar-item .e-tbar-btn {
    background: transparent;
    border: none;
  }

  .e-richtexteditor .e-toolbar .e-toolbar-item .e-tbar-btn:hover {
    background: #e5e7eb;
  }

  /* Read-only state */
  &.read-only .e-richtexteditor {
    background: #f9fafb;
  }

  &.read-only .e-toolbar {
    display: none;
  }
`;

const ReadOnlyOverlay = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    cursor: not-allowed;
  }
`;

// ==================== COMPONENT ====================

export type EX1NotesToManagementWidgetProps = {
  title?: string;
};

export const EX1NotesToManagementWidget: React.FC<
  EX1NotesToManagementWidgetProps
> = ({ title = 'Notes' }) => {
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const annualReportId = selectedReport?.annualReportId;

  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const rteRef = useRef<RichTextEditorComponent | null>(null);
  const lastSavedContent = useRef<string>('');

  // Load content on mount
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setContent('');
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric19(annualReportId);
        if (!isMounted) return;
        setContent(data.content || '');
        lastSavedContent.current = data.content || '';
      } catch (err: any) {
        if (!isMounted) return;
        setContent('');
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Notes to Management.',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  // Auto-save on blur
  const handleBlur = useCallback(async () => {
    if (!annualReportId || !isAdmin) return;
    const currentContent = rteRef.current?.value || '';
    // Skip if content hasn't changed
    if (currentContent === lastSavedContent.current) return;
    try {
      await updateDashboardMetric19(annualReportId, currentContent);
      lastSavedContent.current = currentContent;
      message.success('Notes saved successfully');
    } catch (err: any) {
      message.error(err?.message || 'Failed to save notes');
    } finally {
    }
  }, [annualReportId, isAdmin]);

  // Handle content change
  const handleChange = useCallback(() => {
    // Content is managed by RTE internally
  }, []);

  // Enable spellcheck after RTE is created
  const handleCreated = useCallback(() => {
    if (rteRef.current) {
      try {
        // For div mode (contenteditable)
        const editableElement = rteRef.current.contentModule?.getEditPanel?.();
        if (editableElement) {
          editableElement.setAttribute('spellcheck', 'true');
        }
        // For iframe mode
        const iframeDoc = rteRef.current.contentModule?.getDocument?.();
        if (iframeDoc?.body) {
          iframeDoc.body.setAttribute('spellcheck', 'true');
        }
      } catch (error) {
        console.warn('Could not enable spellcheck on RTE', error);
      }
    }
  }, []);

  // Toolbar items based on requirements: Bold, Bullet points
  const toolbarSettings = {
    enable: isAdmin,
    enableFloating: false,
    items: ['Bold', 'Italic', '|', 'UnorderedList', 'OrderedList'],
  };

  return (
    <Card title={title}>
      {loading ? (
        <div
          style={{
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : errorMessage ? (
        <Alert
          type="error"
          message="Dashboard Metric 19 Error"
          description={errorMessage}
          showIcon
        />
      ) : (
        <>
          <RichTextEditorWrapper className={!isAdmin ? 'read-only' : ''}>
            {isAdmin ? (
              <RichTextEditorComponent
                ref={rteRef}
                value={content}
                change={handleChange}
                blur={handleBlur}
                created={handleCreated}
                toolbarSettings={toolbarSettings}
                quickToolbarSettings={{
                  link: ['Open', 'Edit', 'UnLink'],
                }}
              >
                <Inject services={[Toolbar, HtmlEditor, Link, QuickToolbar]} />
              </RichTextEditorComponent>
            ) : (
              <ReadOnlyOverlay>
                <RichTextEditorComponent
                  ref={rteRef}
                  value={content}
                  readonly={true}
                  toolbarSettings={{ enable: false }}
                >
                  <Inject services={[HtmlEditor]} />
                </RichTextEditorComponent>
              </ReadOnlyOverlay>
            )}
          </RichTextEditorWrapper>
        </>
      )}
    </Card>
  );
};

export default EX1NotesToManagementWidget;
